import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Mail, ShieldCheck, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { getDeviceId, getDeviceLabel } from "@/lib/device";

export default function AdminLogin() {
  const { user, login, adoptToken } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  // Default to Owner on first visit so the master login works out of the box.
  // Once an advisor authenticates on a device, remember it so they land on
  // the Advisor tab next time.
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("propertic_login_tab") || "owner"; }
    catch { return "owner"; }
  });
  const pickTab = (t) => {
    setTab(t);
    try { localStorage.setItem("propertic_login_tab", t); } catch {}
  };

  if (user) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-propertic-bg">
      <div className="hidden lg:block relative bg-propertic-navy">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.25), transparent 60%), radial-gradient(circle at 70% 70%, rgba(43,75,119,0.5), transparent 55%)"
        }} />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div>
            <div className="inline-flex items-center bg-white rounded-xl px-3 py-2">
              <img src="/propertic-logo.png" alt="The Propertic" className="h-12 w-auto object-contain" />
            </div>
            <p className="mt-3 font-mono-brand text-propertic-gold-2/90">CRM Console</p>
          </div>
          <div>
            <p className="font-mono-brand text-propertic-gold mb-3">Advisor sign-in</p>
            <p className="font-editorial text-4xl leading-tight max-w-md">
              Advisor console. <br />
              <span className="text-propertic-gold italic">Manage the pipeline.</span>
            </p>
            <p className="mt-8 text-white/70 text-sm max-w-md leading-relaxed">
              First-time access is gated by an OTP delivered to the owner. Once approved, this device unlocks with a 4-digit PIN.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-editorial text-xl text-propertic-navy hover:text-propertic-gold transition-colors">The Propertic</Link>
          <h1 className="font-editorial text-3xl mt-8 text-propertic-navy">{t("admin.login_title")}</h1>

          <div className="mt-6 inline-flex rounded-full border hairline bg-white overflow-hidden" data-testid="login-tabs">
            <button
              onClick={() => pickTab("advisor")}
              data-testid="login-tab-advisor"
              className={`h-9 px-4 text-[11px] font-mono-brand tracking-[0.14em] uppercase transition-colors ${tab === "advisor" ? "bg-propertic-navy text-propertic-paper" : "text-propertic-navy hover:bg-propertic-cream"}`}
            >Advisor</button>
            <button
              onClick={() => pickTab("owner")}
              data-testid="login-tab-owner"
              className={`h-9 px-4 text-[11px] font-mono-brand tracking-[0.14em] uppercase transition-colors ${tab === "owner" ? "bg-propertic-navy text-propertic-paper" : "text-propertic-navy hover:bg-propertic-cream"}`}
            >Owner</button>
          </div>

          <div className="mt-6">
            {tab === "owner" ? (
              <OwnerLogin login={login} onDone={() => nav("/admin")} />
            ) : (
              <AdvisorLogin adoptToken={adoptToken} onDone={() => nav("/admin")} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OwnerLogin({ login, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      try { localStorage.setItem("propertic_login_tab", "owner"); } catch {}
      toast.success("Signed in.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="owner-login-form">
      <label className="block">
        <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">Owner email</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="owner-login-email" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none" />
      </label>
      <label className="block">
        <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">Password</span>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="owner-login-password" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none" />
      </label>
      <button type="submit" disabled={busy} data-testid="owner-login-submit" className="btn-gold rounded-full w-full h-11 text-xs font-mono-brand tracking-widest disabled:opacity-60">
        {busy ? "…" : "Sign in"}
      </button>
      <div className="text-center">
        <Link
          to="/admin/forgot-password"
          data-testid="owner-forgot-password-link"
          className="text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-muted hover:text-propertic-gold transition-colors"
        >
          Forgot password?
        </Link>
      </div>
    </form>
  );
}

function AdvisorLogin({ adoptToken, onDone }) {
  const [step, setStep] = useState("id");
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const deviceId = getDeviceId();
  const deviceLabel = getDeviceLabel();

  // Normalise the user_id the same way the backend does: lowercase, trim,
  // and strip a leading "@" (Instagram/Twitter handle habit — Ramzi kept
  // typing "@ramzimanneh1" so the OTP flow silently failed). Applied on
  // every call so the three endpoints below always see the same value.
  const normUserId = () => userId.trim().toLowerCase().replace(/^@+/, "").trim();

  const requestOtp = async () => {
    const uid = normUserId();
    if (!uid) { toast.error("Enter your User ID first."); return; }
    setBusy(true);
    try {
      const r = await api.post("/advisor-auth/request-otp", { user_id: uid, device_id: deviceId });
      setOtpSent(true);
      setStep("otp");
      // Backend returns advisor_exists=false when the user_id has no
      // matching active advisor. We surface it as a clear "account not
      // provisioned" hint instead of a mystery "OTP sent" that never
      // arrives — otherwise the advisor waits on a code that will never
      // be generated because the account itself doesn't exist yet.
      if (r.data.advisor_exists === false) {
        toast.error("Your account is not provisioned yet. Ask the CRM owner to add you at /admin/advisors before requesting an OTP.", { duration: 8000 });
      } else if (r.data.delivered_via_email) {
        toast.success("OTP emailed to the owner. Check the CRM inbox.");
      } else {
        toast.warning("OTP generated but email delivery failed. Owner can pull it from /admin, ask them to share it.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request OTP.");
    } finally { setBusy(false); }
  };

  const tryPin = async (e) => {
    e?.preventDefault?.();
    const uid = normUserId();
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be exactly 4 digits."); return; }
    setBusy(true);
    try {
      const r = await api.post("/advisor-auth/pin-login", { user_id: uid, device_id: deviceId, pin });
      adoptToken(r.data.access_token, r.data.user);
      toast.success(`Welcome back, ${r.data.user.full_name}`);
      onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || "PIN login failed.";
      toast.info(msg + " Sending an OTP for this device instead…");
      await requestOtp();
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    const uid = normUserId();
    if (!/^\d{6}$/.test(otp)) { toast.error("OTP must be 6 digits."); return; }
    if (!/^\d{4}$/.test(pin)) { toast.error("Set a 4-digit PIN."); return; }
    setBusy(true);
    try {
      const r = await api.post("/advisor-auth/verify-otp", {
        user_id: uid,
        device_id: deviceId,
        code: otp,
        pin,
        device_label: deviceLabel,
      });
      adoptToken(r.data.access_token, r.data.user);
      toast.success(`Signed in, ${r.data.user.full_name}. This device is now trusted.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed.");
    } finally { setBusy(false); }
  };

  if (step === "id") {
    return (
      <form onSubmit={(e) => { e.preventDefault(); if (userId.trim()) setStep("pin"); }} className="space-y-5" data-testid="advisor-login-id-step">
        <label className="block">
          <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">User ID</span>
          <input value={userId} onChange={(e) => setUserId(e.target.value)} required autoFocus data-testid="advisor-login-userid" placeholder="e.g. ahmed" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none tabular" />
        </label>
        <button type="submit" data-testid="advisor-login-continue" className="btn-gold rounded-full w-full h-11 text-xs font-mono-brand tracking-widest">Continue</button>
        <p className="text-[11px] text-propertic-muted leading-relaxed">
          No User ID? Ask the owner to create one for you. First-time login on this device will request an OTP.
        </p>
      </form>
    );
  }

  if (step === "pin") {
    return (
      <form onSubmit={tryPin} className="space-y-5" data-testid="advisor-login-pin-step">
        <p className="text-[12px] text-propertic-muted"><span className="font-mono-brand text-propertic-gold">{userId}</span> · {deviceLabel}</p>
        <label className="block">
          <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> 4-digit PIN</span>
          <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" pattern="[0-9]*" maxLength={4} autoFocus data-testid="advisor-login-pin" placeholder="••••" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-lg text-center tracking-[0.6em] font-editorial focus:border-propertic-gold outline-none" />
        </label>
        <button type="submit" disabled={busy} data-testid="advisor-login-pin-submit" className="btn-gold rounded-full w-full h-11 text-xs font-mono-brand tracking-widest disabled:opacity-60">
          {busy ? "…" : "Sign in"}
        </button>
        <button type="button" onClick={requestOtp} disabled={busy} data-testid="advisor-login-request-otp" className="w-full h-10 rounded-full border hairline text-[11px] font-mono-brand tracking-[0.14em] uppercase text-propertic-navy hover:border-propertic-gold hover:text-propertic-gold transition-colors inline-flex items-center justify-center gap-2">
          <Mail className="w-3 h-3" /> First time here, request OTP
        </button>
        <button type="button" onClick={() => setStep("id")} className="text-[11px] text-propertic-muted hover:text-propertic-navy">← Use a different User ID</button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="advisor-login-otp-step">
      <div className="rounded-2xl bg-propertic-sand/60 border border-propertic-gold/30 p-3.5 text-[12px] text-propertic-navy leading-relaxed flex items-start gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-propertic-gold mt-0.5 shrink-0" />
        <div>
          {otpSent ? "OTP sent to the owner's inbox. " : "OTP generated. "}
          Ask them to share the 6-digit code with you. Then set a 4-digit PIN for this device.
        </div>
      </div>
      <label className="block">
        <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">OTP (from owner)</span>
        <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus data-testid="advisor-login-otp" placeholder="••••••" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-lg text-center tracking-[0.5em] font-editorial focus:border-propertic-gold outline-none" />
      </label>
      <label className="block">
        <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">Set 4-digit PIN for this device</span>
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" pattern="[0-9]*" maxLength={4} data-testid="advisor-login-newpin" placeholder="••••" className="mt-2 w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-lg text-center tracking-[0.6em] font-editorial focus:border-propertic-gold outline-none" />
      </label>
      <button type="submit" disabled={busy} data-testid="advisor-login-verify" className="btn-gold rounded-full w-full h-11 text-xs font-mono-brand tracking-widest disabled:opacity-60">
        {busy ? "…" : "Verify & trust this device"}
      </button>
      <button type="button" onClick={requestOtp} disabled={busy} className="w-full text-[11px] text-propertic-muted hover:text-propertic-navy inline-flex items-center justify-center gap-1.5">
        <RefreshCw className="w-3 h-3" /> Resend OTP
      </button>
      <button type="button" onClick={() => setStep("pin")} className="w-full text-[11px] text-propertic-muted hover:text-propertic-navy">← Back</button>
    </form>
  );
}
